<template>
  <div>
    <div class="document">
      <div class="header">
        <EsHeader>
          <template #header>
            System Message
          </template>
          <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <a role="button"
                    @keypress.enter.prevent="editPanel()"
                    @click="editPanel()"
                    :class="{ disabled: editOtherPanels }"
                    v-if="!editCurrentPanel"
                    :tabindex="editOtherPanels ? -1 : 0"
                  >
                    <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                  </a>
                </div>
              </li>
            </ul>
          </template>
        </EsHeader>
      </div>
      <div class="content">
        <es-loader v-if="loading" style="margin: 3rem"/>
          <table v-else>
            <tbody>
              <tr>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Status
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons is-pulled-left"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="status = option.code"
                        :class="{'is-active': status === option.code}"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-left is-pulled-left"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :readonly="!editCurrentPanel"
                      :value="this.status === 'false' ? 'Disabled' : 'Enabled'"
                      :aria-label="this.status === 'false' ? 'Disabled' : 'Enabled'"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                  id="msgLabel"
                >
                  Message
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <textarea
                      class="textarea has-text-left is-pulled-left vertical-align"
                      :class="{ 'is-static': !editCurrentPanel,
                        'disabled-textarea': !editCurrentPanel,
                        'is-danger': isMessageError }"
                      :readonly="!editCurrentPanel"
                      type="text"
                      maxlength="500"
                      v-model="message"
                      aria-labelledby="msgLabel"
                    />
                    <p v-if="isMessageError" class="help is-danger">Message is required</p>
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                  id="quickTipLabel"
                >
                  Quick Tip:
                </th>
                <td class="no-border">
                  <div class="control">
                    <textarea
                      class="textarea disabled-textarea property"
                      readonly
                      type="text"
                      rows="4"
                      aria-labelledby="quickTipLabel"
                    >The System Message will display up to 3 lines with no more than 180 characters in the System Message Dashboard panel. Any additional lines or characters beyond 180 will be displayed in a modal screen.
                    </textarea>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
      <div
        v-if="editCurrentPanel"
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 2rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click.prevent="cancelPanel()"
          style="margin-right: 1rem"
        >
          Cancel
        </button>
        <button
          class="button is-primary"
          @click.prevent="updatePanel()"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faTimes,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsHeader,
    EsLoader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return {
      editCurrentPanel: false,
      loading: false,
      message: '',
      status: false,
      type: 'SYSTEM',
    };
  },
  created() {
    this.initialize(this.properties);
    this.enabledDisabled = [
      {
        code: 'true',
        description: 'Enabled',
      },
      {
        code: 'false',
        description: 'Disabled',
      },
    ];
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    isMessageError() {
      return this.status === 'true' && this.message.trim() === '';
    },
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    initialize(data) {
      this.message = data['SYSTEM_MESSAGE-MESSAGE'];
      this.status = data['SYSTEM_MESSAGE-STATUS'];
      this.type = data['SYSTEM_MESSAGE-TYPE'];
    },
    cancelPanel() {
      this.editCurrentPanel = false;
      this.initialize(this.properties);
      this.$emit('allowEditOtherPanels');
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.status = this.status;
      this.$emit('allowEditOtherPanels');
    },
    async updatePanel() {
      if (this.isMessageError === true) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      try {
        this.loading = true;
        this.hideNotification();
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        await this.initProperties();
        this.showNotification('success', ['The System Message has been updated successfully.']);
        this.editCurrentPanel = false;
        this.$emit('allowEditOtherPanels');
      } catch (error) {
        const apiMsg = 'There was an issue. Please try again or contact your administrator.';
        const dataerrors = error.response.data.map(o => o.error);
        this.showNotification('danger', dataerrors || [apiMsg]);
      } finally {
        this.loading = false;
      }
    },
    prepForRequest() {
      const payload = [];
      payload.push({ category: 'SYSTEM_MESSAGE', property: 'MESSAGE', value: this.message });
      payload.push({ category: 'SYSTEM_MESSAGE', property: 'STATUS', value: this.status });
      payload.push({ category: 'SYSTEM_MESSAGE', property: 'TYPE', value: this.type });
      // For 'TYPE', the value will only be "SYSTEM" until further types are implemented.
      return payload;
    },
  },
};
</script>


<style lang="sass" scoped>
  table th
    vertical-align: top
    padding-top: 1em
  table td
    vertical-align: top
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .header
    display: flex
    align-items: center
  .icon-right
    padding-right: 1rem
  .no-border
    border: 0

  .disabled-textarea
    border: 0
    resize: none
    overflow: auto
    padding-left: 0;
    padding-right: 0;

  .property
    padding-left: 0
    font-size: 12px
    width: 25%
  .buttons
    float: right
</style>
